import React, { useState } from 'react';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { get } from 'lodash';
import { BigNumber } from 'bignumber.js';
import { formActions } from 'app/state/actions';
import { TokenService, HolderService } from 'services';
import { getApproveDvDContent, getRejectDvDContent, getCancelDvDContent } from '../Confirmation';
import { getProvider } from 'app/helpers/investor';
import constants from 'app/components/trexConstants';
import { convertBNtoString, convertStringToBN } from 'app/contexts/ClientContext';
import { datetimeFormat, dsConfirm } from '@tokenyico/design-system';
import {
  DsIcon,
  DsButton,
  DsExplorerLink,
  DsLoader,
  DsEthAvatar,
} from '@tokenyico/design-system/dist/reactJS';
import RequestStatus from '../RequestStatus';
import useWeb3ModalWithCallback from 'app/helpers/useWeb3ModalWithCallback';
import { getAddress } from 'viem';

const Loading = () => (
  <div className="_flex:center">
    <DsLoader version="dots" color="var(--primary)" />
  </div>
);

const DvdTransferRequestView = ({
  request,
  selectedToken,
  onCloseModal,
  setError,
  setSuccess,
  holder: { tokens, user },
}) => {
  const { t } = useTranslation();
  const dvdTransferContract = get(
    constants.dvdTransferManagersContracts,
    selectedToken.blockchainNetwork?.toLowerCase(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [errorWeb3, setErrorWeb3] = useState();
  const [action, setAction] = useState('');

  const { callContract, onConnectWallet, isWeb3ModalOpen } = useWeb3ModalWithCallback(async () => {
    switch (action) {
      case 'approve':
        await handleApprove();
        break;
      case 'cancel':
        await handleCancel();
        break;
      default:
        break;
    }
    setAction('');
  });

  useAsync(async () => {
    try {
      if (request) {
        const [recipient, stableCoins] = await Promise.all([
          HolderService.getHolderByWallet(selectedToken.id, getAddress(request.params.taker)),
          TokenService.getStableCoins(),
        ]);

        const requester = await HolderService.getHolderByWallet(
          selectedToken.id,
          getAddress(request.params.maker),
        );
        const tokenMaker = tokens.find(
          (token) => token.address?.toLowerCase() === request.params.token1?.toLowerCase(),
        );
        const tokenTaker = stableCoins.find((token) => {
          return token.address?.toLowerCase() === request.params.token2?.toLowerCase();
        });

        /*
          maker allow it DVDContract to send SCOIN from his balance
          maker initiate the transfer from SCOIN to receive back USDT

          taker allow it DVDContract to send USDT from his balance
          taker accept the transfer, will receive SCOIN
        */

        const isCreatedByMe = request.requesterEmail === user.email;
        let walletsAndStatus;
        // is maker
        if (isCreatedByMe) {
          // token is TREX
          if (tokenTaker.identityRegistryStorage) {
            // if is maker get wallet for taker token
            walletsAndStatus = getProvider({
              wallets: user.walletsAndStatus,
              searchFor: tokenTaker.identityRegistryStorage,
            });
          } else {
            // token is ERC20 => take first
            walletsAndStatus = user.walletsAndStatus[0];
          }
        } else {
          if (tokenMaker.identityRegistryStorage) {
            // if is counterpart get wallet for maker token
            walletsAndStatus = getProvider({
              wallets: user.walletsAndStatus,
              searchFor: tokenMaker.identityRegistryStorage,
            });
          } else {
            // token is ERC20 => take first
            walletsAndStatus = user.walletsAndStatus[0];
          }
        }

        setSelectedRequest({
          isCreatedByMe,
          provider: walletsAndStatus.provider,
          wallet: walletsAndStatus.wallet,
          status: request.status,
          createdAt: request.createdAt,
          transferId: request.params.transferId,
          requester: {
            address: request.params.maker,
            name: requester.investor,
          },
          counterPartyInvestor: {
            address: request.params.taker,
            name: recipient.investor,
          },
          tokenMaker: {
            ...tokenMaker,
            amount: convertBNtoString(request.params.token1Amount, tokenMaker.decimals),
          },
          tokenTaker: {
            ...tokenTaker,
            amount: convertBNtoString(request.params.token2Amount, tokenTaker.decimals), //Stalbe coins 2 decimals
          },
        });
      }
    } catch (e) {
      setError(e);
    }
  }, [request]);

  /* approve methods */
  const handleApprove = async () => {
    if (selectedRequest.provider === 'INTEGRATED') {
      setErrorWeb3(t('validation.walletTypeNotSupported'));
      return;
    }
    const isWalletConnected = onConnectWallet(() => setAction('approve'));
    if (!isWalletConnected) return;

    if (
      await dsConfirm({
        heading: `${t('shared.approveDvDTransfer')}?`,
        content: getApproveDvDContent(t, selectedRequest),
        action: `${t('shared.yes')}, ${t('shared.approveDvDTransfer')}`,
        version: 'success',
      })
    ) {
      handleApproveConfirmed();
      setAction('');
    }
  };

  const handleApproveWeb3 = async (allowanceInput, approveDvdInput) => {
    //check balance taker
    const { result } = await callContract('balance', {
      tokenAddress: allowanceInput.tokenAddress,
    });
    const balance = result;

    if (new BigNumber(balance).isLessThan(allowanceInput.amount)) {
      throw new Error(t('shared.errorInsufficientBalance'));
    }

    return callContract('approve', allowanceInput).then(() =>
      callContract('approveDVDTransfer', approveDvdInput),
    );
  };

  const handleError = async (error) => {
    console.error(error.message);
    setErrorWeb3(error.reason || error.message); // error.reason has priority because error.message can be too long to display
  };

  const handleApproveConfirmed = async () => {
    setIsSubmitting(true);
    setErrorWeb3(null);
    try {
      if (selectedRequest.provider === 'ONCHAINID') {
        await TokenService.postDvdTransferRequestApprove({
          tokenId: selectedRequest.tokenMaker.id,
          requestId: request.id,
          result: 'APPROVED',
          // reason: '',
        });
      } else {
        // as counterpart  allow it DVDContract to send USDT from his balance
        const allowanceInput = {
          tokenAddress: selectedRequest.tokenTaker.address, // increase allowance for taker token
          fromAddress: selectedRequest.wallet,
          toAddress: dvdTransferContract, //as spender
          amount: convertStringToBN(
            selectedRequest.tokenTaker.amount,
            selectedRequest.tokenTaker.decimals,
          ),
        };

        await handleApproveWeb3(allowanceInput, {
          transferId: selectedRequest.transferId,
        });
      }

      setSuccess(t('shared.operationSuccessful'));
      onCloseModal(true);
    } catch (error) {
      handleError(error);
    }
    setIsSubmitting(false);
  };

  /* reject methods */
  const handleReject = async () => {
    if (
      await dsConfirm({
        heading: `${t('shared.rejectDvDTransfer')}?`,
        content: getRejectDvDContent(t, selectedRequest),
        action: `${t('shared.yes')}, ${t('shared.rejectDvDTransfer')}`,
        version: 'danger',
      })
    ) {
      handleRejectConfirmed();
    }
  };

  const handleRejectConfirmed = async () => {
    setIsSubmitting(true);
    setErrorWeb3(null);
    try {
      await TokenService.postDvdTransferRequestApprove({
        tokenId: selectedRequest.tokenMaker.id,
        requestId: request.id,
        result: 'REJECTED',
        // reason: '',
      });

      setSuccess(t('shared.operationSuccessful'));
      onCloseModal(true);
    } catch (error) {
      handleError(error);
    }
    setIsSubmitting(false);
  };

  /* cancel methods */
  const handleCancel = async () => {
    if (selectedRequest.provider === 'INTEGRATED') {
      setErrorWeb3(t('validation.walletTypeNotSupported'));
      return;
    }
    const isWalletConnected = onConnectWallet(() => setAction('cancel'));
    if (!isWalletConnected) return;

    if (
      await dsConfirm({
        heading: `${t('shared.cancelDvdTransfer')}?`,
        content: getCancelDvDContent(t, selectedRequest),
        action: `${t('shared.yes')}, ${t('shared.cancelDvdTransfer').toLocaleLowerCase()}`,
        version: 'danger',
      })
    ) {
      handleCancelConfirmed();
      setAction('');
    }
  };

  const handleCancelWeb3 = async (data) => {
    return callContract('cancelDVDTransfer', data);
  };

  //action only for maker
  const handleCancelConfirmed = async () => {
    setIsSubmitting(true);
    setErrorWeb3(null);
    try {
      if (selectedRequest.provider === 'ONCHAINID') {
        await TokenService.postDvdTransferRequestCancel({
          tokenId: selectedRequest.tokenMaker.id,
          requestId: request.id,
        });
      } else {
        await handleCancelWeb3({
          transferId: selectedRequest.transferId,
        });
      }

      setSuccess(t('shared.operationSuccessful'));
      onCloseModal(true);
    } catch (error) {
      handleError(error);
    }
    setIsSubmitting(false);
  };

  if (!request || !selectedRequest)
    return (
      <div style={{ minWidth: '30rem', minHeight: '30rem' }}>
        <div className="_flex:row _content-space-between:x  _m:top(2) _m:bottom(4) _p:left(2)  _p:right(4)">
          <div className="_flex:align(center)">
            <DsIcon name="transfer" size="22px" class="_m:right(2)" />
            <h1 className="h4">
              {t('shared.dvdTransfer')} {t('shared.request').toLocaleLowerCase()}
            </h1>
          </div>

          <RequestStatus status={request.status} size="md" iconSize="14px" />
        </div>
        <div className="_align:center" style={{ marginTop: '33%' }}>
          <DsLoader
            version="dots"
            style={{ display: 'inline-block' }}
            color="var(--primary)"
            size="5rem"
          />
        </div>
      </div>
    );

  const counterPartInvestorName = selectedRequest.isCreatedByMe
    ? selectedRequest.counterPartyInvestor.name
    : `(${t('shared.me')})`;

  const requesterName = selectedRequest.isCreatedByMe
    ? `(${t('shared.me')})`
    : selectedRequest.requester.name;

  const tokenToSend = selectedRequest.isCreatedByMe
    ? selectedRequest.tokenMaker
    : selectedRequest.tokenTaker;

  const tokenToReceive = selectedRequest.isCreatedByMe
    ? selectedRequest.tokenTaker
    : selectedRequest.tokenMaker;

  return (
    <>
      <div style={{ maxWidth: '48rem', width: '100%' }}>
        <div className="_flex:row _content-space-between:x  _m:top(2) _m:bottom(4) _p:left(2)  _p:right(4)">
          <div className="_flex:align(center)">
            <DsIcon name="transfer" size="22px" class="_m:right(2)" />
            <h1 className="h4">
              {t('shared.dvdTransfer')} {t('shared.request').toLocaleLowerCase()}
            </h1>
          </div>

          <RequestStatus status={request.status} size="md" iconSize="14px" />
        </div>

        <div className="_text:sm">
          {selectedRequest ? (
            <>
              <div
                className="_grid _p:y(4) _p:x(5) _m:bottom(4)"
                style={{
                  backgroundColor: 'var(--grey-200)',
                  marginLeft: '-1.5rem',
                  marginRight: '-1.5rem',
                }}
              >
                <div className="_col(12) _col:md(5) _flex:column">
                  <div className="_flex:column _m:bottom(3)">
                    <div className="_text(500)">{t('shared.tokensToSend')}</div>
                    <div className="_text:xs _text-grey-900">{t('shared.tokensToSendDesc')}</div>
                  </div>

                  <div
                    className="_p(3) _shadow(2) _flex:align(center)"
                    style={{
                      backgroundColor: 'var(--white)',
                      borderRadius: `calc(var(--border-radius) * 1.5)`,
                    }}
                  >
                    <div className="_flex:align(center)">
                      <div className="_flex:align(center)" style={{ height: '2.6rem' }}>
                        <img height="100%" src={tokenToSend.image} alt={tokenToSend.symbol} />
                      </div>
                      <div className="_flex:column _flex:justify(center) _m:left(3)">
                        <span className="_text:xs _text-grey-900">{tokenToSend.name}</span>
                        <div className="_flex:align(center)">
                          <span
                            className="h4 _m:right(2) _text-overflow:ellipsis"
                            style={{ maxWidth: '7.5rem' }}
                          >
                            {tokenToSend.amount}
                          </span>
                          <span className="h5">{tokenToSend.symbol}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="_col(12) _col:md(2) _content-center _text-grey-800 _m:top(5)">
                  <DsIcon name="transfer" size="28px" />
                </div>

                <div className="_col(12) _col:md(5) _flex:column">
                  <div className="_flex:column _m:bottom(3)">
                    <div className="_text(500)">{t('shared.tokensToReceive')}</div>
                    <div className="_text:xs _text-grey-900">{t('shared.tokensToReceiveDesc')}</div>
                  </div>
                  <div
                    className="_shadow(2) _p(3) _flex:align(center)"
                    style={{
                      backgroundColor: 'var(--white)',
                      borderRadius: `calc(var(--border-radius) * 1.5)`,
                    }}
                  >
                    <div className="_flex:align(center)">
                      <div className="_flex:align(center)" style={{ height: '2.6rem' }}>
                        <img height="100%" src={tokenToReceive.image} alt={tokenToReceive.symbol} />
                      </div>
                      <div className="_flex:column _flex:justify(center) _m:left(3)">
                        <span className="_text:xs _text-grey-900">{tokenToReceive.name}</span>
                        <div className="_flex:align(center)">
                          <span
                            className="h4 _m:right(2) _text-overflow:ellipsis"
                            style={{ maxWidth: '7.5rem' }}
                          >
                            {tokenToReceive.amount}
                          </span>
                          <span className="h5">{tokenToReceive.symbol}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="_grid _p:x(3) _m(2)">
                <div className="_col(12) _p(2) border" style={{ borderRadius: ' 0.3rem' }}>
                  <div className="_grid border-bottom _p(2)">
                    <div className="_col(12) _col:md(4) _text:xs _text-grey-900 _content-align:top">
                      {t('shared.requester')}
                    </div>
                    <div className="_col(12) _col:md(8)">
                      <div style={{ flex: 1 }} className="_content-center:y _m:bottom(2)">
                        <DsEthAvatar address={selectedRequest.requester.address} size={14} />
                        <span className="_m:x(2)">{requesterName}</span>
                      </div>
                      {selectedRequest.requester && (
                        <DsExplorerLink
                          className="_m:x(2)"
                          address={selectedRequest.requester.address || '-'}
                          network={selectedToken.blockchainNetwork}
                        />
                      )}
                    </div>
                  </div>

                  <div className="_grid border-bottom _p(2)">
                    <div className="_col(12) _col:md(4) _text:xs _text-grey-900 _content-align:top">
                      {t('shared.counterpartInvestor')}
                    </div>
                    <div className="_col(12) _col:md(8)">
                      <div style={{ flex: 1 }} className="_content-center:y _m:bottom(2)">
                        <DsEthAvatar
                          address={selectedRequest.counterPartyInvestor.address}
                          size={14}
                        />
                        <span className="_m:x(2)">{counterPartInvestorName}</span>
                      </div>

                      {selectedRequest.counterPartyInvestor.address && (
                        <DsExplorerLink
                          address={selectedRequest.counterPartyInvestor.address || '-'}
                          network={selectedToken.blockchainNetwork}
                        />
                      )}
                    </div>
                  </div>
                  <div className="_grid  _p(2)">
                    <div className="_col(12) _col:md(4) _text:xs  _text-grey-900 _content-align:top">
                      {t('shared.createdAt')}
                    </div>
                    <div className="_col(12) _col:md(8)">
                      {datetimeFormat(selectedRequest.createdAt)}
                    </div>
                  </div>
                </div>

                {errorWeb3 && (
                  <div className="_col(12) _m:top(2)">
                    <div className="help-error">
                      <div className="_align:center">{errorWeb3}</div>
                    </div>
                  </div>
                )}

                <div className="_col(12) _m:top(5)">
                  <div className="_flex:justify(end)">
                    <DsButton
                      version="monochrome"
                      label={t('shared.close')}
                      type="button"
                      onClick={() => onCloseModal(false)}
                      className="_p:x(2)"
                    />

                    {selectedRequest.status === 'PENDING_APPROVAL' && (
                      <>
                        {selectedRequest.isCreatedByMe && (
                          <DsButton
                            version="error"
                            label={t('shared.cancelDvdTransfer')}
                            type="button"
                            onClick={handleCancel}
                            isLoading={isSubmitting || isWeb3ModalOpen}
                            className="_p:x(2)"
                            icon="close"
                            isReverse
                          />
                        )}

                        {!selectedRequest.isCreatedByMe && (
                          <>
                            <DsButton
                              version="error"
                              label={t('shared.reject')}
                              type="button"
                              onClick={handleReject}
                              isLoading={isSubmitting || isWeb3ModalOpen}
                              className="_p:x(2)"
                              icon="close"
                              isReverse
                            />
                            <DsButton
                              version="success"
                              label={t('shared.approve')}
                              type="button"
                              onClick={handleApprove}
                              isLoading={isSubmitting || isWeb3ModalOpen}
                              className="_p:x(2)"
                              icon="checkmark"
                              isReverse
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </>
  );
};

const mapToProps = (state) => state;
export default connect(mapToProps, formActions)(DvdTransferRequestView);
